<template>
  <BaseModal :modalTitle="$t('W: Opret ordre')">
    <!-- Step indicator (hidden on confirmation step) -->
    <div
      v-if="currentStep < 5"
      class="mb-4 flex items-center gap-1.5">
      <span
        class="text-xs font-medium tracking-wider text-text-muted uppercase">
        {{ $t('area.dhl.wizard.step-of-total', { step: currentStep, total: 4 }) }}
      </span>
      <span class="text-text-muted">·</span>
      <span class="text-sm font-semibold text-text-primary">
        {{ currentStepTitle }}
      </span>
    </div>

    <!-- Step 1: Customer -->
    <div v-if="currentStep === 1">
      <UiBaseFormVee
        submitOnCtrlEnter
        :errorText="errorText"
        :onSubmit="onStep1Next">
        <div class="mb-4 flex items-center gap-2">
          <button
            type="button"
            :class="[
              'rounded-full px-3.5 py-1.5 text-sm font-medium transition-colors',
              form.customerType === 'business'
                ? 'bg-action text-white'
                : 'bg-surface-muted text-text-secondary hover:bg-surface-sunken',
            ]"
            @click="form.customerType = 'business'">
            {{ $t('W: Erhverv') }}
          </button>
          <button
            type="button"
            :class="[
              'rounded-full px-3.5 py-1.5 text-sm font-medium transition-colors',
              form.customerType === 'insurance'
                ? 'bg-action text-white'
                : 'bg-surface-muted text-text-secondary hover:bg-surface-sunken',
            ]"
            @click="form.customerType = 'insurance'">
            {{ $t('W: Forsikring') }}
          </button>
          <button
            type="button"
            :class="[
              'rounded-full px-3.5 py-1.5 text-sm font-medium transition-colors',
              form.customerType === 'private'
                ? 'bg-action text-white'
                : 'bg-surface-muted text-text-secondary hover:bg-surface-sunken',
            ]"
            @click="form.customerType = 'private'">
            {{ $t('W: Privat') }}
          </button>
        </div>

        <UiComboboxVee
          v-if="form.customerType === 'business'"
          name="customerAccountID"
          :label="$t('W: Kunde')"
          :rules="['required']"
          isRequired
          showAsRequired
          v-model="form.customerAccountID"
          :options="customerAccountOptions" />

        <!-- MIL-402: an insurance customer orders on its own behalf — no insurer picker and
        no insurance-case toggle below, since the order is always its own insurance case. -->
        <UiComboboxVee
          v-if="form.customerType === 'insurance'"
          name="insuranceCustomerAccountID"
          :label="$t('W: Forsikringskunde')"
          :rules="['required']"
          isRequired
          showAsRequired
          v-model="form.insuranceCustomerAccountID"
          :options="insuranceCustomerAccountOptions" />

        <template v-if="form.customerType !== 'private'">
          <template v-if="!isCreatingNewCustomerUser">
            <UiComboboxVee
              name="customerUserID"
              :label="$t('W: Bruger')"
              :rules="['required']"
              isRequired
              showAsRequired
              :isDisabled="!selectedCompanyCustomer"
              v-model="form.customerUserID"
              :options="customerUserOptions" />

            <button
              v-if="canCreateCustomerUserInline"
              type="button"
              class="mt-2 mb-4 cursor-pointer self-start text-sm text-action hover:underline"
              @click="onStartCreateCustomerUser">
              {{ $t('W: Opret ny bruger') }}
            </button>
          </template>

          <!-- MIL-401: the user is only created once the order is actually placed
          (see onCreate) — never here — so abandoning the wizard leaves no orphan
          user behind. Whether the user gets a welcome mail follows the account's opt-in
          (MIL-397); the line under this frame's heading says which. -->
          <div
            v-else
            class="mb-4 flex flex-col gap-1 rounded-lg border border-border-subtle p-4">
            <div class="mb-2 flex items-center justify-between">
              <span class="text-sm font-semibold text-text-primary">
                {{ $t('W: Ny bruger') }}
              </span>
              <button
                type="button"
                class="cursor-pointer text-sm text-action hover:underline"
                @click="onCancelCreateCustomerUser">
                {{ $t('W: Vælg eksisterende bruger') }}
              </button>
            </div>

            <InfoText
              v-if="!isNewCustomerUserWelcomeEmailEnabled"
              class="mb-2"
              theme="warning"
              :text="noWelcomeEmailWarningText" />
            <p
              v-else
              class="mb-2 text-xs text-text-subtle">
              {{ newCustomerUserWelcomeEmailIntroText }}
            </p>

            <UiInputTextFieldVee
              name="newCustomerUserFirstName"
              :label="$t('form.label.first-name')"
              :placeholder="$t('form.placeholder.first-name')"
              :rules="['required']"
              isRequired
              showAsRequired
              v-model="newCustomerUser.firstName" />

            <UiInputTextFieldVee
              name="newCustomerUserLastName"
              :label="$t('form.label.last-name')"
              :placeholder="$t('form.placeholder.last-name')"
              :rules="['required']"
              isRequired
              showAsRequired
              v-model="newCustomerUser.lastName" />

            <UiInputTextFieldVee
              name="newCustomerUserEmail"
              :label="$t('form.label.email')"
              :placeholder="$t('form.placeholder.email')"
              :rules="['required', 'email']"
              isRequired
              showAsRequired
              type="email"
              v-model="newCustomerUser.email">
              <template v-if="EMU.isEmulatingFirestore" #trailingIcon>
                <button
                  type="button"
                  class="pointer-events-auto flex h-5 w-5 cursor-pointer items-center justify-center opacity-50 hover:opacity-100"
                  v-tooltip="$t('W: Indsæt dummy-email')"
                  @click="insertFakeCustomerUserEmail">
                  <IconLucideSparkles class="h-5 w-5" />
                </button>
              </template>
            </UiInputTextFieldVee>

            <UiInputPhoneVee
              name="newCustomerUserPhone"
              :label="$t('form.label.phone')"
              :rules="['requiredPhone', 'phone']"
              isRequired
              showAsRequired
              v-model="newCustomerUser.phone" />

          </div>
        </template>

        <UiComboboxVee
          v-if="form.customerType === 'private'"
          name="privateUserID"
          :label="$t('W: Privatkunde')"
          :rules="['required']"
          isRequired
          showAsRequired
          v-model="form.privateUserID"
          :options="customerUsersPrivateOptions" />

        <UiInputTextFieldVee
          name="addressLine"
          :label="$t('W: Sagsadresse')"
          :rules="['required']"
          isRequired
          showAsRequired
          v-model="form.address.addressLine">
          <template v-if="isMagicAddressEnabled" #trailingIcon>
            <MagicAddressButton
              :currentAddressLine="form.address.addressLine"
              @on-fill="onMagicAddress" />
          </template>
        </UiInputTextFieldVee>

        <div class="grid grid-cols-2 gap-4">
          <UiInputTextFieldVee
            name="postalCode"
            :label="$t('W: Postnummer')"
            :rules="['required']"
            isRequired
            showAsRequired
            v-model="form.address.postalCode" />

          <UiInputTextFieldVee
            name="city"
            :label="$t('W: By')"
            :rules="['required']"
            isRequired
            showAsRequired
            v-model="form.address.city" />
        </div>

        <!-- An insurance customer's own order always needs its skadesnummer (mandatory since
             2026-10-23) and is not asked for a sagsnummer (EFI, 2026-10-29). A business
             customer's Forsikringssag keeps its own optional field further down. -->
        <UiInputTextFieldVee
          v-if="form.customerType === 'insurance'"
          name="insuranceNumber"
          :label="$t('W: Skadesnummer')"
          :rules="['required']"
          isRequired
          showAsRequired
          v-model="form.insuranceNumber" />

        <UiInputTextFieldVee
          v-if="form.segment === 'business' && form.customerType !== 'insurance'"
          name="caseNumber"
          :label="$t('W: Sagsnummer')"
          v-model="form.caseNumber" />

        <div
          v-if="canMarkAsInsuranceCase"
          class="mt-4">
          <div class="mb-4 flex items-center gap-2">
            <AppSwitch
              :isActive="form.isInsuranceCase"
              :title="$t('W: Forsikringssag')"
              @on-toggle="form.isInsuranceCase = !form.isInsuranceCase" />
            <span>{{ $t('W: Forsikringssag') }}</span>
          </div>

          <template v-if="form.isInsuranceCase">
            <UiComboboxVee
              name="insurancePartnerID"
              :label="$t('W: Forsikringskunde')"
              :rules="['required']"
              isRequired
              showAsRequired
              v-model="form.insurancePartnerID"
              :options="insurancePartnerOptions" />

            <UiInputTextFieldVee
              name="insuranceNumber"
              :label="$t('W: Skadesnummer')"
              v-model="form.insuranceNumber" />
          </template>
        </div>

        <template #buttons>
          <UiBaseButton
            :text="$t('action.next')"
            :isLoading="isLoading"
            type="submit" />
        </template>
      </UiBaseFormVee>
    </div>

    <!-- Step 2: Samples (area + analyses + TAT per sample) -->
    <div v-else-if="currentStep === 2">
      <p
        v-if="customerHasNoAvailableAnalyses"
        class="text-sm text-text-muted">
        {{
          $t(
            'W: Ingen analyser tilgænge­lige for denne konto. Tjek venligst prissegmentet.'
          )
        }}
      </p>
      <div
        v-else
        class="flex flex-col gap-4">
        <!-- Progress header -->
        <div>
          <span class="text-sm text-text-muted">
            {{ confirmedCount }} / {{ form.samples.length }}
            {{ $t('W: prøver bekræftet') }}
          </span>
        </div>

        <!-- Sample cards -->
        <div class="flex flex-col gap-2">
          <div
            v-for="(sample, index) in form.samples"
            :key="index"
            ref="sampleCards">
            <!-- COLLAPSED STATE -->
            <div
              v-if="!sample.isExpanded"
              class="flex cursor-pointer items-center gap-3 rounded-lg border border-feedback-success bg-feedback-success-bg px-4 py-2.5 hover:bg-feedback-success-bg"
              @click="expandSample(index)">
              <IconLucideCircleCheck
                class="h-4 w-4 flex-shrink-0 text-feedback-success" />
              <span class="font-mono text-xs font-semibold text-text-primary">
                {{ sample.sampleCode }}
              </span>
              <span class="text-text-muted">·</span>
              <span class="truncate text-xs text-text-secondary">
                {{ getSampleAreaName(sample.sampleAreaID) }}
              </span>
              <span class="text-text-muted">·</span>
              <span class="flex-1 truncate text-xs text-text-secondary">
                {{
                  sample.analysisIDs.map((id) => getAnalysisName(id)).join(', ')
                }}
              </span>
              <span class="text-xs whitespace-nowrap text-text-secondary">
                {{ getTatName(sample.tatID) }}
              </span>
              <button
                v-if="form.samples.length > 1"
                type="button"
                class="ml-1 flex-shrink-0 text-text-muted hover:text-feedback-error"
                @click.stop="removeSample(index)">
                <IconLucideTrash2 class="h-3.5 w-3.5" />
              </button>
            </div>

            <!-- EXPANDED STATE -->
            <div
              v-else
              class="flex flex-col gap-3 rounded-lg border border-border-subtle p-4">
              <!-- Card header -->
              <div class="flex items-center justify-between">
                <span class="text-sm font-semibold">
                  {{ $t('W: Prøve') }} {{ index + 1 }}
                </span>
                <button
                  v-if="form.samples.length > 1"
                  type="button"
                  class="text-text-muted hover:text-feedback-error"
                  @click="removeSample(index)">
                  <IconLucideTrash2 class="h-4 w-4" />
                </button>
              </div>

              <!-- Label badge -->
              <div class="flex items-center gap-3 rounded-lg bg-surface-sunken px-4 py-3">
                <div
                  class="rounded border-2 border-border-strong bg-surface px-4 py-2 font-mono text-lg font-bold tracking-wider text-text-primary">
                  {{ sample.sampleCode }}
                </div>
                <span class="flex items-center gap-1.5 text-xs text-text-muted">
                  <IconLucidePencil class="h-3.5 w-3.5 shrink-0" />
                  {{ $t('W: Skriv dette på prøven') }}
                </span>
              </div>

              <!-- Section: Analysis + TAT -->
              <div class="mt-1 flex flex-col gap-1">
                <p class="text-xs font-medium tracking-wider text-text-muted uppercase">
                  {{ $t('W: Analyse og svartid') }}
                </p>

                <div class="grid grid-cols-2 gap-4">
                  <!-- Sample area -->
                  <UiComboboxVee
                    :name="`sample-${index}-sampleAreaID`"
                    :label="$t('W: Prøveområde')"
                    :rules="['required']"
                    isRequired
                    showAsRequired
                    v-model="sample.sampleAreaID"
                    :options="sampleAreaOptions"
                    @update:model-value="onSampleAreaChange(index)" />

                  <!-- Analyses -->
                  <UiComboboxMultiVee
                    :name="`sample-${index}-analysisIDs`"
                    :label="$t('W: Analyser')"
                    isRequired
                    showAsRequired
                    :options="analysisOptionsForSample(index)"
                    :modelValue="sample.analysisIDs"
                    :isDisabled="!sample.sampleAreaID"
                    closeOnSelect
                    @update:model-value="
                      onAnalysisSelectionChange(index, $event)
                    " />
                </div>

                <!-- TAT -->
                <div class="flex flex-col gap-1">
                  <UiComboboxVee
                    :name="`sample-${index}-tatID`"
                    :label="$t('W: Svartid')"
                    :rules="['required']"
                    isRequired
                    showAsRequired
                    v-model="sample.tatID"
                    :options="tatOptionsForSample(index)"
                    :isDisabled="sample.analysisIDs.length === 0" />
                  <p
                    v-if="
                      sample.analysisIDs.length > 0 &&
                      availableTatsForSample(index).length === 0
                    "
                    class="text-sm text-text-muted">
                    {{
                      $t(
                        'W: Ingen svartider tilgængelige. Tjek venligst priskonfigurationen.'
                      )
                    }}
                  </p>
                </div>
              </div>

              <!-- Section: Sample information -->
              <div class="flex flex-col gap-1 border-t border-border-subtle pt-4">
                <p class="text-xs font-medium tracking-wider text-text-muted uppercase">
                  {{ $t('W: Prøveinformation') }}
                </p>

                <UiInputTextFieldVee
                  :name="`sample-${index}-material`"
                  :label="$t('W: Materiale')"
                  :rules="['maxLength:50']"
                  v-model="sample.material" />

                <div class="grid grid-cols-2 gap-4">
                  <UiInputTextFieldVee
                    :name="`sample-${index}-builtYear`"
                    type="number"
                    :label="$t('W: Opførelsesår')"
                    v-model="sample.builtYear" />

                  <UiInputTextFieldVee
                    :name="`sample-${index}-buildingLocation`"
                    :label="$t('W: Sted')"
                    :rules="['maxLength:100']"
                    v-model="sample.buildingLocation" />
                </div>

                <UiInputTextFieldVee
                  :name="`sample-${index}-additionalNotes`"
                  :label="$t('W: Note')"
                  :rules="['maxLength:250']"
                  v-model="sample.additionalNotes" />
              </div>

              <!-- Confirm section -->
              <div class="border-t border-border-subtle pt-3">
                <label
                  class="flex items-center gap-2.5"
                  :class="{
                    'cursor-not-allowed opacity-50':
                      !isSampleReadyToConfirm(index),
                    'cursor-pointer': isSampleReadyToConfirm(index),
                  }">
                  <input
                    type="checkbox"
                    :checked="sample.isLabelConfirmed"
                    :disabled="!isSampleReadyToConfirm(index)"
                    class="h-4 w-4 flex-shrink-0 rounded border-border-strong accent-action"
                    @change="onLabelConfirmedChange(index)" />
                  <span class="text-sm text-text-primary">
                    {{ $t('W: Jeg har skrevet koden på prøven') }}
                  </span>
                </label>
                <p
                  v-if="!isSampleReadyToConfirm(index)"
                  class="mt-1.5 pl-6 text-xs text-text-muted">
                  {{ $t('W: Udfyld alle felter for at bekræfte') }}
                </p>
              </div>

              <!-- Validation error -->
              <p
                v-if="sampleErrors[index]"
                class="text-sm text-feedback-error">
                {{ sampleErrors[index] }}
              </p>
            </div>
          </div>
        </div>

        <UiBaseButton
          theme="neutral"
          :text="$t('W: Tilføj prøve')"
          @button-click="addSample" />
      </div>
    </div>

    <!-- Step 3: Drop point -->
    <div v-else-if="currentStep === 3">
      <p
        v-if="noCompatibleDropPoints && !isDhlEnabled"
        class="text-sm text-text-muted">
        {{
          $t(
            'W: Ingen tilgængelige drop points. Tjek walk-in svartidskonfigurationen.'
          )
        }}
      </p>
      <div
        v-else
        class="flex flex-col gap-3">
        <!-- Non-DHL: flat list (existing behaviour). Deliberately lab drop points
        only — private drop points and DHL are dev-actions-only, see isDhlEnabled. -->
        <template v-if="!isDhlEnabled">
          <div
            v-for="dp in selectableDropPoints"
            :key="dp.id"
            class="cursor-pointer rounded-lg border p-4 transition-colors"
            :class="
              form.dropPointID === dp.id
                ? 'border-action bg-surface-muted'
                : 'border-border-subtle hover:border-border-strong'
            "
            @click="form.dropPointID = dp.id">
            <p class="text-sm font-semibold">{{ dp.getName(currentLocale) }}</p>
            <p class="text-sm text-text-secondary">
              {{ dp.address.address }}, {{ dp.address.postalCode }}
              {{ dp.address.city }}
            </p>
            <p
              v-if="dp.getPlacement(currentLocale)"
              class="mt-1 text-xs text-text-muted">
              {{ dp.getPlacement(currentLocale) }}
            </p>
          </div>
        </template>

        <!-- DHL enabled: grouped sections -->
        <template v-if="isDhlEnabled">
          <!-- Lab drop points -->
          <template v-if="availableLabDropPoints.length > 0">
            <p
              class="text-xs font-medium tracking-wider text-text-muted uppercase">
              {{ $t('area.dhl.drop-point.section.lab') }}
            </p>
            <div
              v-for="dp in availableLabDropPoints"
              :key="dp.id"
              class="cursor-pointer rounded-lg border p-4 transition-colors"
              :class="
                form.dropPointID === dp.id
                  ? 'border-action bg-surface-muted'
                  : 'border-border-subtle hover:border-border-strong'
              "
              @click="form.dropPointID = dp.id">
              <p class="text-sm font-semibold">
                {{ dp.getName(currentLocale) }}
              </p>
              <p class="text-sm text-text-secondary">
                {{ dp.address.address }}, {{ dp.address.postalCode }}
                {{ dp.address.city }}
              </p>
              <p
                v-if="dp.getPlacement(currentLocale)"
                class="mt-1 text-xs text-text-muted">
                {{ dp.getPlacement(currentLocale) }}
              </p>
            </div>
          </template>

          <!-- Private drop points — dev-actions-only, see isDhlEnabled -->
          <template v-if="availablePrivateDropPoints.length > 0">
            <div
              class="flex items-center gap-2"
              :class="{ 'mt-2': availableLabDropPoints.length > 0 }">
              <DevBadge />
              <span class="text-xs font-medium tracking-wider text-text-muted uppercase">
                {{ $t('area.dhl.drop-point.section.private') }}
              </span>
            </div>
            <div
              v-for="dp in availablePrivateDropPoints"
              :key="dp.id"
              class="cursor-pointer rounded-lg border p-4 transition-colors"
              :class="
                form.dropPointID === dp.id
                  ? 'border-action bg-surface-muted'
                  : 'border-border-subtle hover:border-border-strong'
              "
              @click="form.dropPointID = dp.id">
              <p class="text-sm font-semibold">
                {{ dp.getName(currentLocale) }}
              </p>
              <p class="text-sm text-text-secondary">
                {{ dp.address.address }}, {{ dp.address.postalCode }}
                {{ dp.address.city }}
              </p>
              <p
                v-if="dp.getPlacement(currentLocale)"
                class="mt-1 text-xs text-text-muted">
                {{ dp.getPlacement(currentLocale) }}
              </p>
              <p class="mt-1 text-xs text-text-muted">
                <span v-if="dhlCutoffCapabilityByDropPointID[dp.id] === 'loading'">
                  {{ $t('area.dhl.drop-point.message.loading-cutoff') }}
                </span>
                <span
                  v-else-if="
                    dhlCutoffCapabilityByDropPointID[dp.id] &&
                    (
                      dhlCutoffCapabilityByDropPointID[dp.id] as {
                        localCutoffDateAndTime: string | null
                      }
                    ).localCutoffDateAndTime &&
                    new Date(
                      (
                        dhlCutoffCapabilityByDropPointID[dp.id] as {
                          localCutoffDateAndTime: string | null
                        }
                      ).localCutoffDateAndTime as string
                    ) > now
                  ">
                  {{
                    $t('area.dhl.drop-point.message.cutoff-today', {
                      time: formatDhlCutoffTime(
                        (
                          dhlCutoffCapabilityByDropPointID[dp.id] as {
                            localCutoffDateAndTime: string | null
                          }
                        ).localCutoffDateAndTime as string
                      ),
                    })
                  }}
                </span>
                <span
                  v-else-if="
                    dhlCutoffCapabilityByDropPointID[dp.id] &&
                    (
                      dhlCutoffCapabilityByDropPointID[dp.id] as {
                        localCutoffDateAndTime: string | null
                      }
                    ).localCutoffDateAndTime &&
                    new Date(
                      (
                        dhlCutoffCapabilityByDropPointID[dp.id] as {
                          localCutoffDateAndTime: string | null
                        }
                      ).localCutoffDateAndTime as string
                    ) <= now
                  ">
                  {{ $t('area.dhl.drop-point.message.cutoff-passed-today') }}
                </span>
              </p>
            </div>
          </template>

          <!-- DHL service points — dev-actions-only, see isDhlEnabled -->
          <div
            class="flex items-center gap-2"
            :class="{ 'mt-2': availableDropPoints.length > 0 }">
            <DevBadge />
            <span class="text-xs font-medium tracking-wider text-text-muted uppercase">
              {{ $t('area.dhl.service-point.title') }}
            </span>
          </div>
          <div class="flex gap-2">
            <input
              v-model="servicePointSearchAddress"
              type="text"
              :placeholder="$t('form.label.address')"
              class="bg-surface-subtle min-w-0 flex-1 rounded-lg border border-border-subtle px-3 py-2 text-sm outline-none focus:border-action"
              @keyup.enter="searchServicePoints" />
            <UiBaseButton
              :text="$t('term.search')"
              :isLoading="isSearchingServicePoints"
              @button-click="searchServicePoints" />
          </div>
          <p
            v-if="servicePointSearchError"
            class="text-state-error text-sm">
            {{ servicePointSearchError }}
          </p>
          <!-- Selected service point -->
          <div
            v-if="selectedServicePoint"
            class="rounded-lg border border-action bg-surface-muted p-4">
            <p class="text-sm font-semibold">{{ selectedServicePoint.name }}</p>
            <p class="text-sm text-text-secondary">
              {{ selectedServicePoint.address.addressLine }},
              {{ selectedServicePoint.address.postalCode }}
              {{ selectedServicePoint.address.city }}
            </p>
            <p
              v-if="selectedServicePoint.distance"
              class="mt-1 text-xs text-text-muted">
              {{
                $t('area.dhl.service-point.message.distance-away', { dist: selectedServicePoint.distance })
              }}
            </p>
            <button
              class="mt-2 cursor-pointer text-xs text-text-muted transition-colors hover:text-text-primary"
              @click="selectedServicePoint = null">
              {{ $t('area.dhl.service-point.action.choose-different') }}
            </button>
          </div>
          <!-- Search results -->
          <div
            v-else-if="servicePointSearchResults.length > 0"
            class="flex flex-col gap-2">
            <div
              v-for="sp in servicePointSearchResults"
              :key="sp.servicePointID"
              class="cursor-pointer rounded-lg border border-border-subtle p-4 transition-colors hover:border-border-strong"
              @click="selectServicePoint(sp)">
              <p class="text-sm font-semibold">{{ sp.name }}</p>
              <p class="text-sm text-text-secondary">
                {{ sp.address.addressLine }}, {{ sp.address.postalCode }}
                {{ sp.address.city }}
              </p>
              <p
                v-if="sp.distance"
                class="mt-1 text-xs text-text-muted">
                {{ $t('area.dhl.service-point.message.distance-away', { dist: sp.distance }) }}
              </p>
            </div>
          </div>
        </template>
      </div>
    </div>

    <!-- Step 4: Delivery date -->
    <div v-else-if="currentStep === 4">
      <!-- TODO(MIL-156): re-enable once order priority is fully wired up — lab-cloud's
      createOrder schema/service never persist this value, so every order silently saves
      as 'medium' regardless of what's picked here. Hidden until that's finished.
      <div class="mb-4 flex flex-col gap-1.5">
        <span class="text-sm font-semibold text-text-primary">
          {{ $t('W: Prioritet') }}
        </span>
        <div class="flex gap-2">
          <button
            v-for="opt in priorityOptions"
            :key="opt.value"
            type="button"
            class="cursor-pointer rounded-lg border px-4 py-2 text-sm transition-colors"
            :class="
              form.priority === opt.value
                ? 'border-action bg-surface-muted font-semibold'
                : 'border-border-subtle hover:border-border-strong'
            "
            @click="form.priority = opt.value">
            {{ opt.text }}
          </button>
        </div>
      </div>
      -->

      <p
        v-if="availableDeliveryDates.length === 0"
        class="text-sm text-text-muted">
        <span v-if="isDhlCapabilityLoading">
          {{ $t('area.dhl.drop-point.message.loading-availability') }}
        </span>
        <span v-else-if="selectedDropPoint?.type === 'private'">
          {{
            $t(
              'area.dhl.drop-point.message.no-pickup-10-working-days'
            )
          }}
        </span>
        <span v-else>
          {{
            $t(
              'W: Ingen tilg\u00e6ngelige leveringsdatoer. Kontroll\u00e9r drop pointens \u00e5bningstider.'
            )
          }}
        </span>
      </p>
      <div
        v-else
        class="flex flex-col gap-3">
        <div
          v-for="date in availableDeliveryDates"
          :key="date.toISOString()"
          class="cursor-pointer rounded-lg border p-4 transition-colors"
          :class="
            form.deliveryDate && form.deliveryDate.getTime() === date.getTime()
              ? 'border-action bg-surface-muted'
              : 'border-border-subtle hover:border-border-strong'
          "
          @click="form.deliveryDate = date">
          <p class="text-sm font-semibold">{{ formatDeliveryDate(date) }}</p>
          <p
            v-if="formatDhlPickupWindow(date)"
            class="mt-0.5 text-xs text-text-muted">
            {{ formatDhlPickupWindow(date) }}
          </p>
          <p
            v-if="formatDhlCutoffForDate(date)"
            class="mt-0.5 text-xs text-text-muted">
            {{ formatDhlCutoffForDate(date) }}
          </p>
          <p
            v-if="formatDhlLabArrival(date)"
            class="mt-0.5 text-xs text-text-muted">
            {{ formatDhlLabArrival(date) }}
          </p>
          <p
            v-if="formatExpectedResultDate(date)"
            class="mt-0.5 text-xs text-text-muted">
            {{ formatExpectedResultDate(date) }}
          </p>
        </div>
      </div>

      <!-- Mark as received — only meaningful when the chosen date is today -->
      <div
        v-if="isSelectedDeliveryDateToday && canUser('orders.registerSamples')"
        class="mt-6 border-t border-border-subtle pt-4">
        <div class="flex items-center gap-2.5">
          <AppSwitch
            :isActive="form.isReceived"
            :title="$t('W: Prøverne er modtaget på laboratoriet')"
            @on-toggle="onToggleReceivedClick" />
          <span class="text-sm text-text-primary">
            {{ $t('W: Prøverne er modtaget på laboratoriet') }}
          </span>
        </div>

        <UiComboboxVee
          v-if="form.isReceived && labLocationOptions.length > 0"
          name="receivingLocationID"
          :label="$t('W: Lab-lokation')"
          class="mt-3"
          :rules="['required']"
          isRequired
          v-model="form.receivingLocationID"
          :options="labLocationOptions" />
      </div>
    </div>

    <!-- Step 5: Order confirmation -->
    <div
      v-else-if="currentStep === 5"
      class="flex flex-col gap-5">
      <div class="flex items-center gap-2">
        <IconLucideCircleCheck class="text-green-600 shrink-0" />
        <span class="font-semibold text-text-primary">
          {{
            $t('area.dhl.order.message.created-with-prefix', {
              prefix: generatedPrefix ?? '',
            })
          }}
        </span>
      </div>

      <!-- DHL shipment section -->
      <div
        v-if="createdOrder && createdOrderDhlShipment"
        class="rounded-lg border border-border-subtle p-4">
        <span
          class="mb-3 block text-xs font-medium tracking-wider text-text-muted uppercase">
          {{ $t('area.dhl.shipment.title') }}
        </span>
        <DhlShipmentCard
          :shipment="createdOrderDhlShipment"
          :orderID="createdOrder.id" />
      </div>

      <!-- Selvlevering — intet DHL forsendelse (walk-in, lab drop point, eller DHL slået fra) -->
      <div
        v-else-if="createdOrder && !isDhlServicePointSelected"
        class="rounded-lg border border-border-subtle p-4">
        <p class="text-sm text-text-secondary">
          {{
            $t(
              'area.dhl.order.message.self-delivery'
            )
          }}
        </p>
      </div>
    </div>

    <template #buttons>
      <template v-if="currentStep > 1 && currentStep < 5">
        <UiBaseButton
          :text="$t('action.back')"
          :isDisabled="isLoading || isLoadingDeliveryDates"
          @button-click="prevStep" />
        <UiBaseButton
          v-if="currentStep < 4"
          :text="
            currentStep === 3 && isDhlServicePointSelected
              ? $t('action.create')
              : $t('action.next')
          "
          :isLoading="
            (isLoading && currentStep === 3 && isDhlServicePointSelected) ||
            (currentStep === 3 && isLoadingDeliveryDates) ||
            (currentStep === 2 && isLoadingDhlCutoffTimes)
          "
          :showSaveIcon="
            showSaveIcon && currentStep === 3 && isDhlServicePointSelected
          "
          :isDisabled="
            (currentStep === 2 && !allSamplesConfirmed) ||
            (currentStep === 3 && !form.dropPointID && !isDhlServicePointSelected)
          "
          @button-click="onNextClick" />
        <UiBaseButton
          v-if="currentStep === 4"
          :text="$t('action.create')"
          :isLoading="isLoading"
          :showSaveIcon="showSaveIcon"
          :isDisabled="
            !form.deliveryDate ||
            (form.isReceived && labLocationOptions.length > 0 && !form.receivingLocationID)
          "
          @button-click="onCreate" />
      </template>

      <template v-else-if="currentStep === 5">
        <UiBaseButton
          :text="$t('area.dhl.action.close')"
          @button-click="closeTop" />
      </template>
    </template>
  </BaseModal>
</template>

<script lang="ts">
import { canUseMagicAddress } from '@/dev/magicAddress'
import type { IMagicAddress } from '@/dev/magicAddress'
import { managementApi } from '@/globals/typescript/utils/api/managementClient'
import { useAccountStore } from '@/stores/accountStore'
import { useClosedDayStore } from '@/stores/closedDayStore'
import { formatDateToStr } from '@/globals/typescript/utils/dates'
import { useOrderStore } from '@/stores/orderStore'
import { useDhlShipmentStore } from '@/stores/dhlShipmentStore'
import { useModalStore } from '@/stores/modalStore'
import { useCustomerAccountStore } from '@/stores/customerAccountStore'
import {
  getNoWelcomeEmailWarningText,
  getWelcomeEmailIntroText,
} from '@/globals/typescript/utils/welcomeEmailNotice'
import { useCustomerUserStore } from '@/stores/customerUserStore'
import type { ICustomerRef, TCustomerType } from '@team-milva/lab-models/customer'
import {
  customerKey,
  isWelcomeEmailEnabledForCustomer,
  type TCompanyCustomerType,
} from '@/globals/typescript/utils/customerOwner'
import { buildCreateOrderCustomerFields } from '@/globals/typescript/utils/createOrderCustomer'
import { useInsuranceCustomerAccountStore } from '@/stores/insuranceCustomerAccountStore'
import type { InsuranceCustomerAccount } from '@team-milva/lab-models/insuranceCustomerAccount'
import { useAnalysisStore } from '@/stores/analysisStore'
import { useAppStore } from '@/stores/appStore'
import { usePricingSegmentStore } from '@/stores/pricingSegmentStore'
import {
  useTurnaroundTimeStore,
  TurnaroundTime,
} from '@/stores/turnaroundTimeStore'
import { useDropPointStore, DropPoint } from '@/stores/dropPointStore'
import { useSampleAreaStore } from '@/stores/sampleAreaStore'
import { useUserStore } from '@/stores/userStore'
import { useLabLocationStore } from '@/stores/labLocationStore'
import { ENV, EMU } from '@/globals/typescript/utils/host'
import { generateFakeEmail } from '@/globals/typescript/utils/fakeEmail'
import type {
  IDhlShipment,
  TOrderPriority,
  TOrderSegment,
} from '@team-milva/lab-models/resources'
import {
  calcDeliveryDate,
  resolveDhlAwareDropoff,
  advanceToWorkingDay,
} from '@team-milva/lab-utils/deliveryDate'
import { mapActions, mapState } from 'pinia'
import { useToast } from '@/globals/typescript/utils/toast'
import { getErrorMessage } from '@/globals/typescript/utils/errorMessages'
import { mergePrices } from '@/globals/typescript/utils/customerAccountPricingUtils'
import {
  isDropPointOpenToday,
  isWithinDropPointWalkInWindow,
} from '@/globals/typescript/utils/labDropPointWalkIn'
import type { PropType } from 'vue'
import { useUnsavedChangesGuard } from '@/composables/useUnsavedChangesGuard'

export default {
  name: 'CreateOrderModal',

  props: {
    onSuccess: {
      type: Function as PropType<(id: string) => void>,
      required: false,
      default: undefined,
    },
  },

  data() {
    return {
      isMagicAddressEnabled: canUseMagicAddress(),
      unsavedGuard: useUnsavedChangesGuard(),
      EMU,
      currentStep: 1 as number,
      // MIL-401: a business user can be filled in here and is created together
      // with the order in onCreate — never before it. Held outside `form` so the
      // order payload never has to be told to ignore it.
      isCreatingNewCustomerUser: false,
      newCustomerUser: {
        firstName: '',
        lastName: '',
        email: '',
        phone: { number: 0, iso2: 'dk' },
      },
      createdOrderID: null as string | null,
      // Returned directly by createOrder now that DHL booking is synchronous — used
      // instead of waiting on the allDhlShipments Firestore listener to catch up so
      // the label/QR shows immediately on the step-5 confirmation screen.
      createdOrderDhlShipmentOverride: null as IDhlShipment | null,
      isLoading: false,
      showSaveIcon: false,
      errorText: '',
      now: new Date(),
      nowTickerInterval: null as ReturnType<typeof setInterval> | null,
      sampleErrors: [] as string[],
      generatedPrefix: null as string | null,
      nextSampleNumber: 1 as number,
      defaultTatID: '',
      isLoadingDhlCutoffTimes: false,
      // Populated in one call by fetchAvailableDeliveryDates — the backend already
      // resolves cutoff-filtering, real-pickup-day dedup, and the 5-result cap
      // (see resolveAvailableDeliveryDates in lab-cloud) instead of looping over
      // every candidate date against our own capability endpoint.
      availableDates: null as
        | {
            date: string
            localCutoffDateAndTime: string | null
            estimatedDeliveryDateAndTime: string | null
            pickupEarliest: string | null
            pickupLatest: string | null
            price: number | null
            priceCurrency: string | null
          }[]
        | null,
      isLoadingDeliveryDates: false as boolean,
      selectedServicePoint: null as {
        servicePointID: string
        name: string
        address: {
          addressLine: string
          city: string
          postalCode: string
          countryCode: string
        }
        distance: string | null
      } | null,
      servicePointSearchAddress: '',
      servicePointSearchResults: [] as {
        servicePointID: string
        name: string
        address: {
          addressLine: string
          city: string
          postalCode: string
          countryCode: string
        }
        distance: string | null
      }[],
      isSearchingServicePoints: false,
      servicePointSearchError: '',
      form: {
        // Which kind of customer the order is placed for (MIL-402). `segment` follows it —
        // an insurance customer's own order is a `'business'` segment order; only the
        // owner (`customer.type`) tells it apart. Set by the customerType watcher.
        customerType: 'business' as TCustomerType,
        segment: 'business' as TOrderSegment,
        priority: 'medium' as TOrderPriority,
        caseNumber: '',
        address: {
          addressLine: '',
          postalCode: '',
          city: '',
        },
        isInsuranceCase: false,
        insurancePartnerID: '',
        insuranceNumber: '',
        customerAccountID: '',
        insuranceCustomerAccountID: '',
        customerUserID: '',
        privateUserID: '',
        samples: [
          {
            analysisIDs: [] as string[],
            sampleCode: '',
            sampleAreaID: '',
            tatID: '',
            material: '',
            builtYear: '',
            buildingLocation: '',
            additionalNotes: '',
            isExpanded: true,
            isLabelConfirmed: false,
          },
        ] as {
          analysisIDs: string[]
          sampleCode: string
          sampleAreaID: string
          tatID: string
          material: string
          builtYear: string
          buildingLocation: string
          additionalNotes: string
          isExpanded: boolean
          isLabelConfirmed: boolean
        }[],
        dropPointID: '',
        deliveryDate: null as Date | null,
        isReceived: false,
        receivingLocationID: '',
      },
    }
  },

  computed: {
    isNewCustomerUserWelcomeEmailEnabled(): boolean {
      return this.selectedCompanyCustomer
        ? isWelcomeEmailEnabledForCustomer(this.selectedCompanyCustomer)
        : false
    },

    // The business or insurance customer the order is for, once one is picked — whose users,
    // private drop points and prices the wizard offers. Null for a private order.
    selectedCompanyCustomer(): (ICustomerRef & { type: TCompanyCustomerType }) | null {
      if (this.form.customerType === 'business' && this.form.customerAccountID) {
        return { type: 'business', id: this.form.customerAccountID }
      }
      if (this.form.customerType === 'insurance' && this.form.insuranceCustomerAccountID) {
        return { type: 'insurance', id: this.form.insuranceCustomerAccountID }
      }
      return null
    },

    newCustomerUserWelcomeEmailIntroText(): string {
      return getWelcomeEmailIntroText()
    },

    noWelcomeEmailWarningText(): string {
      return getNoWelcomeEmailWarningText()
    },

    ...mapState(useAccountStore, ['labAccount']),
    ...mapState(useClosedDayStore, ['closedDates']),
    ...mapState(useCustomerAccountStore, [
      'allCustomerAccounts',
      'allCustomerAccountPrices',
      'customerAccountDisplayNamesByID',
    ]),
    ...mapState(useCustomerUserStore, [
      'customerUsers',
      'customerUsersByCustomerKey',
      'customerUsersPrivate',
      'allCustomerPrivatePrices',
    ]),
    ...mapState(useAnalysisStore, ['allAnalyses']),
    ...mapState(useAppStore, ['currentLocale', 'defaultMessageSaveIconTime']),
    ...mapState(usePricingSegmentStore, ['allPricingSegments']),
    ...mapState(useTurnaroundTimeStore, ['allTATs']),
    ...mapState(useDropPointStore, [
      'labDropPoints',
      'privateDropPoints',
      'dhlCutoffCapabilityByDropPointID',
    ]),
    ...mapState(useSampleAreaStore, ['allSampleAreas']),
    ...mapState(useOrderStore, ['allOrders']),
    ...mapState(useDhlShipmentStore, ['allDhlShipments']),
    ...mapState(useUserStore, ['isDeveloper', 'showDevActions', 'currentUser']),
    ...mapState(useLabLocationStore, ['activeLabLocations']),
    ...mapState(useInsuranceCustomerAccountStore, [
      'allInsuranceCustomerAccounts',
      'allInsuranceCustomerAccountPrices',
    ]),

    createdOrder() {
      if (!this.createdOrderID) return null
      return this.allOrders.find((x) => x.id === this.createdOrderID) ?? null
    },

    createdOrderDhlShipment() {
      if (this.createdOrderDhlShipmentOverride) return this.createdOrderDhlShipmentOverride
      if (!this.createdOrder?.dhlShipmentID) return null
      return (
        this.allDhlShipments.find(
          (x) => x.id === this.createdOrder?.dhlShipmentID
        ) ?? null
      )
    },

    isDhlEnabled(): boolean {
      // DHL shipments are a testing-only feature in lab-management — never
      // show them in production, and only when dev options are enabled.
      if (!ENV.isDev || !this.isDeveloper || !this.showDevActions) return false
      return this.labAccount?.integrations?.dhl?.options?.isEnabled === true
    },

    isDhlServicePointSelected(): boolean {
      return this.selectedServicePoint !== null
    },

    dhlEarlyDelivery(): boolean {
      return this.labAccount?.integrations?.dhl?.options?.earlyDelivery ?? false
    },

    currentStepTitle(): string {
      const titles: Record<number, string> = {
        1: this.$t('W: Kundeoplysninger'),
        2: this.$t('W: Prøver og analyser'),
        3: this.$t('area.dhl.drop-point.label'),
        4: this.$t('area.dhl.drop-point.field.delivery-date'),
      }
      return titles[this.currentStep] ?? ''
    },

    selectedCustomerAccountPriceDoc() {
      if (!this.form.customerAccountID) return null
      return (
        this.allCustomerAccountPrices.find(
          (x) => x.customerAccountID === this.form.customerAccountID
        ) ?? null
      )
    },

    selectedCustomerSegment() {
      const segmentID = this.selectedCustomerAccountPriceDoc?.pricingSegmentID
      if (!segmentID) return null
      return this.allPricingSegments.find((x) => x.id === segmentID) ?? null
    },

    selectedPrivateCustomerPriceDoc() {
      if (!this.form.privateUserID) return null
      return (
        this.allCustomerPrivatePrices.find(
          (x) => x.privateUserID === this.form.privateUserID
        ) ?? null
      )
    },

    selectedPrivateCustomerSegment() {
      const segmentID = this.selectedPrivateCustomerPriceDoc?.pricingSegmentID
      if (!segmentID) return null
      return this.allPricingSegments.find((x) => x.id === segmentID) ?? null
    },

    // An insurance customer's own order is priced, and its orderable set decided, by its own
    // InsuranceCustomerAccountPrices document (MIL-402) — the same one lab-cloud prices it by.
    selectedInsuranceCustomerPriceDoc() {
      if (!this.form.insuranceCustomerAccountID) return null
      return (
        this.allInsuranceCustomerAccountPrices.find(
          (x) => x.insuranceCustomerAccountID === this.form.insuranceCustomerAccountID
        ) ?? null
      )
    },

    selectedInsuranceCustomerSegment() {
      const segmentID = this.selectedInsuranceCustomerPriceDoc?.pricingSegmentID
      if (!segmentID) return null
      return this.allPricingSegments.find((x) => x.id === segmentID) ?? null
    },

    // null = no filter (no customer selected, show all analyses)
    // Set<string> = filter to these IDs (may be empty if no analyses are configured)
    customerAnalysisFilter(): Set<string> | null {
      if (this.form.customerType === 'insurance') {
        if (!this.form.insuranceCustomerAccountID) return null
        const { active } = mergePrices({
          segmentAnalyses: this.selectedInsuranceCustomerSegment?.analyses ?? [],
          customerAnalyses: this.selectedInsuranceCustomerPriceDoc?.analyses ?? [],
          segmentTats: this.selectedInsuranceCustomerSegment?.tats ?? [],
          customerTats: this.selectedInsuranceCustomerPriceDoc?.tats ?? [],
          allAnalyses: this.allAnalyses,
          allTats: this.allTATs,
        })
        return new Set(active.analyses.map((x) => x.analysisID))
      }
      if (this.form.segment === 'business') {
        if (!this.form.customerAccountID) return null
        const segmentEntries = this.selectedCustomerSegment?.analyses ?? []
        const customerEntries =
          this.selectedCustomerAccountPriceDoc?.analyses ?? []
        const { active } = mergePrices({
          segmentAnalyses: segmentEntries,
          customerAnalyses: customerEntries,
          segmentTats: this.selectedCustomerSegment?.tats ?? [],
          customerTats: this.selectedCustomerAccountPriceDoc?.tats ?? [],
          allAnalyses: this.allAnalyses,
          allTats: this.allTATs,
        })
        return new Set(active.analyses.map((x) => x.analysisID))
      }
      if (this.form.segment === 'private') {
        if (!this.form.privateUserID) return null
        const segmentEntries =
          this.selectedPrivateCustomerSegment?.analyses ?? []
        const customerEntries =
          this.selectedPrivateCustomerPriceDoc?.analyses ?? []
        const { active } = mergePrices({
          segmentAnalyses: segmentEntries,
          customerAnalyses: customerEntries,
          segmentTats: this.selectedPrivateCustomerSegment?.tats ?? [],
          customerTats: this.selectedPrivateCustomerPriceDoc?.tats ?? [],
          allAnalyses: this.allAnalyses,
          allTats: this.allTATs,
        })
        return new Set(active.analyses.map((x) => x.analysisID))
      }
      return null
    },

    customerHasNoAvailableAnalyses(): boolean {
      return (
        this.customerAnalysisFilter !== null &&
        this.customerAnalysisFilter.size === 0
      )
    },

    uniqueAnalysisIDs(): string[] {
      const ids = new Set<string>()
      for (const sample of this.form.samples) {
        for (const id of sample.analysisIDs) ids.add(id)
      }
      return [...ids]
    },

    allSamplesHaveTat(): boolean {
      return (
        this.form.samples.length > 0 &&
        this.form.samples.every((s) => s.analysisIDs.length > 0 && !!s.tatID)
      )
    },

    confirmedCount(): number {
      return this.form.samples.filter((x) => !x.isExpanded).length
    },

    allSamplesConfirmed(): boolean {
      return (
        this.form.samples.length > 0 &&
        this.form.samples.every((x) => !x.isExpanded)
      )
    },

    sampleAreaOptions(): { value: string; text: string }[] {
      const filter = this.customerAnalysisFilter
      return this.allSampleAreas
        .filter((x) => x.activeState === 'active')
        .filter((area) =>
          this.allAnalyses.some((analysis) => {
            if (!analysis.sampleAreaIDs.includes(area.id)) return false
            if (filter !== null && !filter.has(analysis.id)) return false
            if (analysis.analysisTestIDs.length === 0) return false
            return true
          })
        )
        .map((area) => ({
          value: area.id,
          text: area.getName(this.currentLocale),
        }))
        .sort((a, b) => a.text.localeCompare(b.text))
    },

    selectedWalkInTatIDs(): string[] {
      const ids: string[] = []
      for (const sample of this.form.samples) {
        if (!sample.tatID) continue
        const tat = this.allTATs.find((x) => x.id === sample.tatID)
        if (tat?.type === 'walk-in' && !ids.includes(tat.id)) ids.push(tat.id)
      }
      return ids
    },

    selectedFixedDeadlineTATs(): TurnaroundTime[] {
      const ids = new Set(
        this.form.samples.map((x) => x.tatID).filter((id) => !!id)
      )
      return this.allTATs.filter(
        (tat) => ids.has(tat.id) && tat.type === 'fixed-deadline'
      )
    },

    availableLabDropPoints(): DropPoint[] {
      const walkInTatIDs = this.selectedWalkInTatIDs
      return this.labDropPoints.filter((dp) => {
        if (dp.activeState !== 'active') return false
        if (!dp.options.useOpeningHours) return false
        if (walkInTatIDs.length === 0) return true
        if (!walkInTatIDs.every((id) => dp.walkInTatIDs.includes(id)))
          return false
        // Walk-in drop points must be open right now today, and within their own walk-in window
        if (!this.isDropPointOpenToday(dp)) return false
        return this.isWithinDropPointWalkInWindow(dp)
      })
    },

    availablePrivateDropPoints(): DropPoint[] {
      const customer = this.selectedCompanyCustomer
      if (!customer) return []
      return this.privateDropPoints.filter(
        (dp) =>
          dp.activeState === 'active' &&
          dp.customer?.type === customer.type &&
          dp.customer.id === customer.id
      )
    },

    availableDropPoints(): DropPoint[] {
      return [
        ...this.availableLabDropPoints,
        ...this.availablePrivateDropPoints,
      ]
    },

    // Private drop points and DHL are dev-actions-only (see isDhlEnabled) —
    // without dev actions on, only lab drop points can ever be selected.
    selectableDropPoints(): DropPoint[] {
      return this.isDhlEnabled
        ? this.availableDropPoints
        : this.availableLabDropPoints
    },

    isDhlCapabilityLoading(): boolean {
      return (
        this.selectedDropPoint?.type === 'private' &&
        this.isLoadingDeliveryDates
      )
    },

    noCompatibleDropPoints(): boolean {
      return this.selectableDropPoints.length === 0
    },

    selectedDropPoint(): DropPoint | null {
      if (!this.form.dropPointID) return null
      return (
        this.labDropPoints.find((x) => x.id === this.form.dropPointID) ??
        this.privateDropPoints.find((x) => x.id === this.form.dropPointID) ??
        null
      )
    },

    availableDeliveryDates(): Date[] {
      const dp = this.selectedDropPoint
      if (!dp) return []

      const now = this.now
      const todayStart = new Date(
        now.getFullYear(),
        now.getMonth(),
        now.getDate()
      )

      // Walk-in orders can only be delivered today — no date selection needed
      if (this.selectedWalkInTatIDs.length > 0) {
        return [new Date(todayStart)]
      }

      type DayKey = 'monday' | 'tuesday' | 'wednesday' | 'thursday' | 'friday'
      const weekdayToKey: (DayKey | null)[] = [
        null,
        'monday',
        'tuesday',
        'wednesday',
        'thursday',
        'friday',
        null,
      ]

      const result: Date[] = []
      const candidate = new Date(todayStart)

      // Private drop points: the backend already resolves the first 5 real pickup
      // days DHL has confirmed it can service (cutoff-filtered, deduped by real
      // pickup day) — see resolveAvailableDeliveryDates / fetchAvailableDeliveryDates.
      if (dp.type === 'private') {
        if (!this.availableDates) return []
        return this.availableDates.map((d) => {
          const [year, month, day] = d.date.split('-').map(Number) as [
            number,
            number,
            number,
          ]
          return new Date(year, month - 1, day)
        })
      }

      // Bounded: `result.length < 5` alone never terminates if every day ahead
      // is a closed day or one the drop point is shut on.
      let searched = 0
      while (result.length < 5 && searched < 366) {
        searched++
        const dayKey = weekdayToKey[candidate.getDay()] ?? null
        const candidateIso = formatDateToStr(candidate)
        if (dayKey !== null && !this.closedDates.includes(candidateIso)) {
          const day = dp.openingHours[dayKey]
          // Today stays selectable even once the clock has passed the drop point's closing
          // time — a user working late can still be handing a sample to the lab in person
          // right now. `expectedResultDate` already anchors on the drop point's *closing*
          // time for whichever date is picked (see below), never the actual current clock
          // time, so a late-in-the-day pick for today already yields the same conservative
          // results-ready estimate it would at any other hour — nothing else needs to change
          // for a same-day pick to still compute the correct (possibly next-working-day)
          // results date once a TAT's own cut-off has passed.
          if (day.isOpen) {
            result.push(new Date(candidate))
          }
        }
        candidate.setDate(candidate.getDate() + 1)
      }

      return result
    },

    // "Mark as received" only ever makes sense against today's date — the sample can't
    // already be at the lab for a delivery that hasn't happened yet.
    isSelectedDeliveryDateToday(): boolean {
      if (!this.form.deliveryDate) return false
      const today = this.now
      return (
        this.form.deliveryDate.getFullYear() === today.getFullYear() &&
        this.form.deliveryDate.getMonth() === today.getMonth() &&
        this.form.deliveryDate.getDate() === today.getDate()
      )
    },

    labLocationOptions(): { value: string; text: string }[] {
      return this.activeLabLocations
        .map((location) => ({ value: location.id, text: location.name }))
        .sort((a, b) => a.text.localeCompare(b.text))
    },

    priorityOptions(): { value: TOrderPriority; text: string }[] {
      return [
        { value: 'low', text: this.$t('W: Lav') },
        { value: 'medium', text: this.$t('W: Middel') },
        { value: 'high', text: this.$t('W: Høj') },
      ]
    },

    customerAccountOptions(): { value: string; text: string }[] {
      return this.allCustomerAccounts
        .filter((x) => x.activeState === 'active')
        .map((customerAccount) => ({
          value: customerAccount.id,
          // Shows " (intern)" so the marker is visible at the moment an order is placed
          // for an internal account, not only afterwards (MIL-252).
          text:
            this.customerAccountDisplayNamesByID?.[customerAccount.id] ??
            customerAccount.name,
        }))
        .sort((a, b) => a.text.localeCompare(b.text))
    },

    customerUsersPrivateOptions(): { value: string; text: string }[] {
      return this.customerUsersPrivate
        .filter((x) => x.activeState === 'active')
        .map((user) => ({
          value: user.id,
          text: user.fullName,
        }))
        .sort((a, b) => a.text.localeCompare(b.text))
    },

    customerUserOptions(): { value: string; text: string }[] {
      const users = this.selectedCompanyCustomer
        ? (this.customerUsersByCustomerKey[customerKey(this.selectedCompanyCustomer)] ?? [])
        : []
      return users
        .filter((x) => x.activeState === 'active')
        .map((user) => ({
          value: user.id,
          text: user.fullName,
        }))
        .sort((a, b) => a.text.localeCompare(b.text))
    },

    // Creating a user from here writes a real customer user, so it is gated by
    // the same permission the Users tab uses — never by orders.create alone.
    canCreateCustomerUserInline(): boolean {
      return !!this.selectedCompanyCustomer && this.canUser('customers.manage')
    },

    // Only active insurance customer accounts linked to the selected customer account — a business
    // order can only be billed to an insurance customer account the customer is actually associated with.
    activeInsuranceCustomerAccountsForCustomer(): InsuranceCustomerAccount[] {
      if (!this.form.customerAccountID) return []
      return this.allInsuranceCustomerAccounts.filter(
        (insuranceCustomerAccount) =>
          insuranceCustomerAccount.activeState === 'active' &&
          insuranceCustomerAccount.customerAccountIDs.includes(this.form.customerAccountID)
      )
    },

    // Insurance is only ever offered for business orders whose customer has at
    // least one active insurance customer account to bill it to.
    canMarkAsInsuranceCase(): boolean {
      return (
        this.form.customerType === 'business' &&
        this.activeInsuranceCustomerAccountsForCustomer.length > 0
      )
    },

    // Active insurance customers only — the customer an order is placed *for*, not an insurer
    // a business order is billed to.
    insuranceCustomerAccountOptions(): { value: string; text: string }[] {
      return this.allInsuranceCustomerAccounts
        .filter((x) => x.activeState === 'active')
        .map((insuranceCustomerAccount) => ({
          value: insuranceCustomerAccount.id,
          text: insuranceCustomerAccount.name,
        }))
        .sort((a, b) => a.text.localeCompare(b.text))
    },

    insurancePartnerOptions(): { value: string; text: string }[] {
      return this.activeInsuranceCustomerAccountsForCustomer
        .map((insuranceCustomerAccount) => ({ value: insuranceCustomerAccount.id, text: insuranceCustomerAccount.name }))
        .sort((a, b) => a.text.localeCompare(b.text))
    },
  },

  watch: {
    'form.deliveryDate'() {
      if (!this.isSelectedDeliveryDateToday) {
        this.form.isReceived = false
        this.form.receivingLocationID = ''
      }
    },

    'form.dropPointID'(newVal: string) {
      if (newVal) {
        this.selectedServicePoint = null
        this.servicePointSearchResults = []
      }
      // Available dates are only fetched on Next click — selecting a drop point
      // just invalidates any dates fetched for a previously selected one.
      this.availableDates = null
    },

    'form.customerType'(newVal: TCustomerType) {
      this.form.segment = newVal === 'private' ? 'private' : 'business'
      // A pending user belongs to the customer it was started on, and a private customer
      // is an account of its own that can never be created on the fly here.
      this.resetNewCustomerUser()
      this.form.customerUserID = ''
      this.form.insuranceNumber = ''
      // An insurance customer has no sagsnummer, so one typed before switching is dropped.
      if (newVal === 'insurance') {
        this.form.caseNumber = ''
      }
      if (newVal !== 'business') {
        this.form.customerAccountID = ''
        this.form.isInsuranceCase = false
        this.form.insurancePartnerID = ''
      }
      if (newVal !== 'insurance') {
        this.form.insuranceCustomerAccountID = ''
      }
      if (newVal !== 'private') {
        this.form.privateUserID = ''
      }
    },

    'form.privateUserID'() {
      if (this.currentStep === 1) {
        this.form.samples = this.form.samples.map((s) => ({
          ...s,
          analysisIDs: [],
          sampleAreaID: '',
          tatID: '',
          isExpanded: true,
          isLabelConfirmed: false,
        }))
        this.sampleErrors = this.form.samples.map(() => '')
      }
    },

    'form.isInsuranceCase'(newVal: boolean) {
      if (!newVal) {
        this.form.insurancePartnerID = ''
        this.form.insuranceNumber = ''
      }
    },

    'form.insuranceCustomerAccountID'() {
      // Users, private drop points, analyses and TATs all belong to the insurance customer.
      this.resetNewCustomerUser()
      this.form.customerUserID = ''
      if (this.currentStep === 1) {
        this.form.samples = this.form.samples.map((s) => ({
          ...s,
          analysisIDs: [],
          sampleAreaID: '',
          tatID: '',
          isExpanded: true,
          isLabelConfirmed: false,
        }))
        this.sampleErrors = this.form.samples.map(() => '')
      }
    },

    'form.customerAccountID'() {
      // The pending user belongs to the account it was started on — a different
      // account means it would be created in the wrong place.
      this.resetNewCustomerUser()
      this.form.customerUserID = ''
      // The insurer list is customer-specific — an insurer valid for the previous
      // customer may not even be linked to the new one.
      this.form.isInsuranceCase = false
      this.form.insurancePartnerID = ''
      this.form.insuranceNumber = ''
      // Reset sample data when the customer account changes because analysis
      // availability and TATs are customer-specific
      if (this.currentStep === 1) {
        this.form.samples = this.form.samples.map((s) => ({
          ...s,
          analysisIDs: [],
          sampleAreaID: '',
          tatID: '',
          isExpanded: true,
          isLabelConfirmed: false,
        }))
        this.sampleErrors = this.form.samples.map(() => '')
      }
    },
  },
  methods: {
    onMagicAddress(address: IMagicAddress) {
      this.form.address.addressLine = address.addressLine
      this.form.address.postalCode = address.postalCode
      this.form.address.city = address.city
    },

    ...mapActions(useOrderStore, ['createOrder', 'generateOrderPrefix', 'registerSamples']),
    ...mapActions(useModalStore, ['closeTop']),
    ...mapActions(useDropPointStore, ['fetchDhlCutoffCapability']),
    ...mapActions(useUserStore, ['canUser']),

    ...mapActions(useCustomerUserStore, ['createCustomerUser']),

    onStartCreateCustomerUser(): void {
      this.errorText = ''
      this.isCreatingNewCustomerUser = true
      this.form.customerUserID = ''
    },

    onCancelCreateCustomerUser(): void {
      this.errorText = ''
      this.resetNewCustomerUser()
    },

    resetNewCustomerUser(): void {
      this.isCreatingNewCustomerUser = false
      this.newCustomerUser = {
        firstName: '',
        lastName: '',
        email: '',
        phone: { number: 0, iso2: 'dk' },
      }
    },

    insertFakeCustomerUserEmail(): void {
      this.newCustomerUser.email = generateFakeEmail()
      this.errorText = ''
    },

    // Catches the common duplicate before the employee fills in the whole order
    // only to be rejected on the last step. The server stays authoritative —
    // email uniqueness is global, not lab-scoped — so onCreate still handles
    // already-exists coming back from the API.
    isNewCustomerUserEmailTaken(): boolean {
      const email = this.newCustomerUser.email.trim().toLowerCase()
      if (!email) return false
      return this.customerUsers.some((x) => x.email?.toLowerCase() === email)
    },

    async onStep1Next(): Promise<void> {
      if (this.isCreatingNewCustomerUser && this.isNewCustomerUserEmailTaken()) {
        this.errorText = getErrorMessage('auth/email-already-exists')
        return
      }
      // Generate prefix on first entry to step 2 — this is the transaction that bumps nextLabPrefixNumber
      if (!this.generatedPrefix) {
        this.isLoading = true
        this.errorText = ''
        try {
          this.generatedPrefix = await this.generateOrderPrefix()
          // Assign code to the initial sample that was pre-populated
          this.form.samples.forEach((sample) => {
            sample.sampleCode = `${this.generatedPrefix}-${this.nextSampleNumber++}`
          })
        } catch {
          this.errorText = this.$t('W: Der opstod en fejl')
          useToast().error(this.$t('W: Kunne ikke generere ordrepræfiks'))
          return
        } finally {
          this.isLoading = false
        }
      }
      this.currentStep = 2
    },

    async onNextClick(): Promise<void> {
      if (this.currentStep === 2) {
        await this.onStep2Next()
        return
      }
      if (this.currentStep === 3) {
        // DHL service point orders skip the delivery date step — the customer drops
        // off the parcel themselves, so no pickup date is scheduled.
        if (this.isDhlServicePointSelected) {
          void this.onCreate()
          return
        }
        // Available dates are fetched here only — not on drop point selection —
        // so the button shows a loader instead of landing on a half-empty Step 4.
        if (this.selectedDropPoint?.type === 'private') {
          await this.fetchAvailableDeliveryDates()
        }
        // Pre-select the date when only one is available, but always advance to
        // step 4 rather than creating outright — see onStep2Next for why an
        // order must never skip past the "mark as received" step unconfirmed.
        if (this.availableDeliveryDates.length === 1) {
          this.form.deliveryDate = this.availableDeliveryDates[0] ?? null
        }
        this.currentStep += 1
        return
      }
      this.currentStep += 1
    },

    async onStep2Next(): Promise<void> {
      const errors = this.form.samples.map((sample) => {
        if (sample.analysisIDs.length === 0)
          return this.$t('W: Tilføj mindst én analyse')
        if (!sample.tatID) return this.$t('W: Vælg en svartid')
        return ''
      })
      this.sampleErrors = errors
      if (errors.some((x) => x !== '')) return
      // Pre-select the drop point when only one is available so the user can confirm with Next.
      // Never auto-skip when DHL is enabled — DHL service points are an open-ended address
      // search with no fixed count, so "only one option" can't be determined up front, and
      // skipping would rob the user of the chance to search for/pick a service point instead.
      if (!this.isDhlEnabled && this.selectableDropPoints.length === 1) {
        // Reset available dates synchronously so availableDeliveryDates evaluates against a
        // clean slate — the watcher fires async and may lag behind a leftover result from a
        // previous selection.
        this.availableDates = null
        this.form.dropPointID = this.selectableDropPoints[0]?.id ?? ''
        // Pre-select the date too when only one is available (always true for walk-in
        // TATs), but never call onCreate() here — landing on step 4 unconfirmed is what
        // let an order be created without the user ever seeing the "mark as received"
        // toggle. The Create button on step 4 always requires its own explicit click.
        if (this.availableDeliveryDates.length === 1) {
          this.form.deliveryDate = this.availableDeliveryDates[0] ?? null
        }
        // Skip step 3 (already selected), go straight to step 4
        this.currentStep = 4
        return
      }
      // Cutoff times were already kicked off for every private drop point when the
      // modal mounted and are cached for the session — this only actually waits
      // when the customer's drop points are still mid-fetch by the time Next is
      // clicked, so the Next button's loader is the exception, not the norm.
      if (this.isDhlEnabled) {
        this.isLoadingDhlCutoffTimes = true
        try {
          await this.fetchDhlCutoffCapability(
            this.availablePrivateDropPoints.map((dp) => dp.id)
          )
        } finally {
          this.isLoadingDhlCutoffTimes = false
        }
      }
      this.currentStep = 3
    },

    addSample(): void {
      const sampleCode = `${this.generatedPrefix}-${this.nextSampleNumber++}`
      const lastConfirmed = [...this.form.samples]
        .reverse()
        .find((s) => !s.isExpanded)
      this.form.samples.push({
        analysisIDs: [],
        sampleCode,
        sampleAreaID: lastConfirmed ? lastConfirmed.sampleAreaID : '',
        tatID: '',
        material: '',
        builtYear: '',
        buildingLocation: '',
        additionalNotes: '',
        isExpanded: true,
        isLabelConfirmed: false,
      })
      this.sampleErrors.push('')
      this.$nextTick(() => {
        const cards = this.$refs.sampleCards as HTMLElement[] | undefined
        if (!cards || cards.length === 0) return
        const lastCard = cards[cards.length - 1]
        if (!lastCard) return
        let parent = lastCard.parentElement
        while (parent && parent.scrollHeight <= parent.clientHeight) {
          parent = parent.parentElement
        }
        if (!parent) return
        const extra =
          lastCard.getBoundingClientRect().bottom -
          parent.getBoundingClientRect().bottom +
          50
        if (extra > 0) parent.scrollBy({ top: extra, behavior: 'smooth' })
      })
    },

    removeSample(index: number): void {
      this.form.samples.splice(index, 1)
      this.sampleErrors.splice(index, 1)
    },

    isSampleReadyToConfirm(sampleIndex: number): boolean {
      const sample = this.form.samples[sampleIndex]
      if (!sample) return false
      return (
        !!sample.sampleAreaID &&
        sample.analysisIDs.length > 0 &&
        !!sample.tatID
      )
    },

    onLabelConfirmedChange(sampleIndex: number): void {
      const sample = this.form.samples[sampleIndex]
      if (!sample) return
      const newValue = !sample.isLabelConfirmed
      if (newValue && !this.isSampleReadyToConfirm(sampleIndex)) return
      sample.isLabelConfirmed = newValue
      if (newValue) {
        if (sample.tatID) {
          this.defaultTatID = sample.tatID
        }
        sample.isExpanded = false
      }
    },

    expandSample(sampleIndex: number): void {
      const sample = this.form.samples[sampleIndex]
      if (!sample) return
      sample.isExpanded = true
      sample.isLabelConfirmed = false
    },

    getSampleAreaName(sampleAreaID: string): string {
      const area = this.allSampleAreas.find((x) => x.id === sampleAreaID)
      return area ? area.getName(this.currentLocale) : sampleAreaID
    },

    getTatName(tatID: string): string {
      const tat = this.allTATs.find((x) => x.id === tatID)
      return tat ? tat.getName(this.currentLocale) : tatID
    },

    buildSamplesPayload(): {
      analysisIDs: string[]
      sampleCode: string
      sampleAreaID: string
      tatID: string
      builtYear?: number
      buildingLocation?: string
      material?: string
      additionalNotes?: string
    }[] {
      return this.form.samples.map((s) => ({
        analysisIDs: s.analysisIDs,
        sampleCode: s.sampleCode,
        sampleAreaID: s.sampleAreaID,
        tatID: s.tatID,
        ...(s.builtYear ? { builtYear: Number(s.builtYear) } : {}),
        ...(s.buildingLocation ? { buildingLocation: s.buildingLocation } : {}),
        ...(s.material ? { material: s.material } : {}),
        ...(s.additionalNotes ? { additionalNotes: s.additionalNotes } : {}),
      }))
    },

    onSampleAreaChange(sampleIndex: number): void {
      const sample = this.form.samples[sampleIndex]
      if (!sample) return
      sample.analysisIDs = []
      sample.tatID = ''
    },

    onAnalysisSelectionChange(sampleIndex: number, newIDs: string[]): void {
      const sample = this.form.samples[sampleIndex]
      if (!sample) return
      const wasEmpty = sample.analysisIDs.length === 0
      sample.analysisIDs = newIDs
      if (newIDs.length === 0) {
        sample.tatID = ''
      } else if (
        sample.tatID &&
        !this.availableTatsForSample(sampleIndex).some(
          (x) => x.id === sample.tatID
        )
      ) {
        sample.tatID = ''
      }
      if (wasEmpty && !sample.tatID && this.defaultTatID) {
        const available = this.availableTatsForSample(sampleIndex)
        if (available.some((x) => x.id === this.defaultTatID)) {
          sample.tatID = this.defaultTatID
        }
      }
      this.sampleErrors[sampleIndex] = ''
    },

    addAnalysisToSample(sampleIndex: number, analysisID: string): void {
      const sample = this.form.samples[sampleIndex]
      if (!sample) return
      const isFirstAnalysis = sample.analysisIDs.length === 0
      sample.analysisIDs.push(analysisID)
      // Keep the current TAT if it is still valid after adding the analysis;
      // otherwise clear it so the user must pick again.
      if (
        sample.tatID &&
        !this.availableTatsForSample(sampleIndex).some(
          (x) => x.id === sample.tatID
        )
      ) {
        sample.tatID = ''
      }
      // On the first analysis, try to auto-apply the default TAT
      if (isFirstAnalysis && !sample.tatID && this.defaultTatID) {
        const available = this.availableTatsForSample(sampleIndex)
        if (available.some((x) => x.id === this.defaultTatID)) {
          sample.tatID = this.defaultTatID
        }
      }
      this.sampleErrors[sampleIndex] = ''
    },

    removeAnalysisFromSample(sampleIndex: number, analysisID: string): void {
      const sample = this.form.samples[sampleIndex]
      if (!sample) return
      sample.analysisIDs = sample.analysisIDs.filter((x) => x !== analysisID)
      // Keep the current TAT if it is still valid after removing the analysis;
      // clear it only if no analyses remain or the TAT is no longer applicable.
      if (
        sample.analysisIDs.length === 0 ||
        (sample.tatID &&
          !this.availableTatsForSample(sampleIndex).some(
            (x) => x.id === sample.tatID
          ))
      ) {
        sample.tatID = ''
      }
    },

    formatDeliveryDate(date: Date): string {
      const formatted = new Intl.DateTimeFormat(
        this.currentLocale === 'da' ? 'da-DK' : 'en-GB',
        {
          weekday: 'long',
          day: 'numeric',
          month: 'long',
          year: 'numeric',
        }
      ).format(date)
      return formatted.charAt(0).toUpperCase() + formatted.slice(1)
    },

    expectedResultDate(
      deliveryDate: Date
    ): { date: Date; hour: number; minute: number } | null {
      const dp = this.selectedDropPoint
      if (!dp || this.selectedFixedDeadlineTATs.length === 0) return null

      const pad = (n: number) => String(n).padStart(2, '0')
      const dateStr = `${deliveryDate.getFullYear()}-${pad(deliveryDate.getMonth() + 1)}-${pad(deliveryDate.getDate())}`

      type DayKey = 'monday' | 'tuesday' | 'wednesday' | 'thursday' | 'friday'
      const weekdayToKey: (DayKey | null)[] = [
        null,
        'monday',
        'tuesday',
        'wednesday',
        'thursday',
        'friday',
        null,
      ]

      let dropoffDateStr = dateStr

      if (dp.type === 'private') {
        // Use the DHL delivery date as the TAT anchor (calcDeliveryDate advances to the
        // first working day automatically) — see resolveDhlAwareDropoff for the
        // earlyDelivery rule.
        const dhlAnchor = resolveDhlAwareDropoff({
          estimatedDeliveryDateAndTime:
            this.capabilityForDeliveryDate(deliveryDate)
              ?.estimatedDeliveryDateAndTime ?? null,
          earlyDelivery: this.dhlEarlyDelivery,
        })
        if (dhlAnchor) {
          dropoffDateStr = dhlAnchor.date
        }
      } else {
        // A lab drop point's opening hours only cover Mon–Fri — reject a
        // delivery date that doesn't resolve to one of those keys.
        const dayKey = weekdayToKey[deliveryDate.getDay()]
        if (!dayKey) return null
      }

      let latest: { date: Date; hour: number; minute: number } | null = null

      for (const tat of this.selectedFixedDeadlineTATs) {
        const result = calcDeliveryDate({
          dropoff: { date: dropoffDateStr },
          tat: { workingDays: tat.workingDays, deadline: tat.deadline },
          closedDates: this.closedDates,
        })
        if (!latest || result.date.getTime() > latest.date.getTime()) {
          latest = result
        }
      }

      return latest
    },

    dhlDateStr(deliveryDate: Date): string {
      const pad = (n: number) => String(n).padStart(2, '0')
      return `${deliveryDate.getFullYear()}-${pad(deliveryDate.getMonth() + 1)}-${pad(deliveryDate.getDate())}`
    },

    // Looks up the resolved capability data for a delivery date shown to the
    // customer — availableDates is already keyed by the real pickup date (the
    // backend resolves pickupAdditionalDays before returning it).
    capabilityForDeliveryDate(deliveryDate: Date) {
      const dateStr = this.dhlDateStr(deliveryDate)
      return this.availableDates?.find((d) => d.date === dateStr)
    },

    formatDhlCutoffForDate(deliveryDate: Date): string {
      const dp = this.selectedDropPoint
      if (!dp || dp.type !== 'private') return ''
      const cutoff =
        this.capabilityForDeliveryDate(deliveryDate)?.localCutoffDateAndTime ??
        null
      if (!cutoff) return ''
      const time = new Intl.DateTimeFormat(
        this.currentLocale === 'da' ? 'da-DK' : 'en-GB',
        { hour: '2-digit', minute: '2-digit', hour12: false }
      ).format(new Date(cutoff))
      return this.$t('area.dhl.shipment.message.drop-off-deadline', { time })
    },

    formatDhlLabArrival(deliveryDate: Date): string {
      const dp = this.selectedDropPoint
      if (!dp || dp.type !== 'private') return ''
      const arrival =
        this.capabilityForDeliveryDate(deliveryDate)
          ?.estimatedDeliveryDateAndTime ?? null
      if (!arrival) return ''
      // Show date only — DHL returns 23:59 as a sentinel meaning "sometime during the day"
      const formatted = new Intl.DateTimeFormat(
        this.currentLocale === 'da' ? 'da-DK' : 'en-GB',
        { weekday: 'long', day: 'numeric', month: 'long' }
      ).format(new Date(arrival))
      const capitalised = formatted.charAt(0).toUpperCase() + formatted.slice(1)
      return this.$t('area.dhl.shipment.message.lab-delivery-date', { date: capitalised })
    },

    formatResultCalculationSteps(deliveryDate: Date): string {
      const dp = this.selectedDropPoint
      if (!dp || this.selectedFixedDeadlineTATs.length === 0) return ''

      const result = this.expectedResultDate(deliveryDate)
      if (!result) return ''

      let anchorDate: Date
      if (dp.type === 'private') {
        const dhlAnchor = resolveDhlAwareDropoff({
          estimatedDeliveryDateAndTime:
            this.capabilityForDeliveryDate(deliveryDate)
              ?.estimatedDeliveryDateAndTime ?? null,
          earlyDelivery: this.dhlEarlyDelivery,
        })
        if (!dhlAnchor) return ''
        const labWd: number[] = this.labAccount?.workingDays ?? [1, 2, 3, 4, 5]
        const [year, month, day] = dhlAnchor.date.split('-').map(Number) as [
          number,
          number,
          number,
        ]
        anchorDate = advanceToWorkingDay(
          new Date(year, month - 1, day),
          labWd,
          this.closedDates
        )
      } else {
        anchorDate = deliveryDate
      }

      const maxWorkingDays = Math.max(
        ...this.selectedFixedDeadlineTATs.map((x) => x.workingDays)
      )

      const fmt = (d: Date) => {
        const s = new Intl.DateTimeFormat(
          this.currentLocale === 'da' ? 'da-DK' : 'en-GB',
          { weekday: 'short', day: 'numeric', month: 'short' }
        ).format(d)
        return s.charAt(0).toUpperCase() + s.slice(1)
      }

      const pad = (n: number) => String(n).padStart(2, '0')
      const time = `${pad(result.hour)}:${pad(result.minute)}`
      const dayLabel = this.$t('area.dhl.drop-point.message.n-days', {
        count: maxWorkingDays,
      })
      return `${fmt(anchorDate)} + ${dayLabel} = ${fmt(result.date)} kl. ${time}`
    },

    formatExpectedResultDate(deliveryDate: Date): string {
      const result = this.expectedResultDate(deliveryDate)
      if (!result) return ''
      const formatted = new Intl.DateTimeFormat(
        this.currentLocale === 'da' ? 'da-DK' : 'en-GB',
        { weekday: 'long', day: 'numeric', month: 'long' }
      ).format(result.date)
      const capitalised = formatted.charAt(0).toUpperCase() + formatted.slice(1)
      const pad = (n: number) => String(n).padStart(2, '0')
      return this.$t('area.dhl.order.message.results-ready-at', {
        date: capitalised,
        time: `${pad(result.hour)}:${pad(result.minute)}`,
      })
    },

    analysisOptionsForSample(
      sampleIndex: number
    ): { value: string; text: string; subtitle?: string }[] {
      const sample = this.form.samples[sampleIndex]
      if (!sample?.sampleAreaID) return []
      const selectedIDs = sample.analysisIDs
      const filter = this.customerAnalysisFilter
      const onlyOnePerSampleSubtitle = (analysis: {
        options?: { isOnlyOnePerSample?: boolean }
      }) =>
        analysis.options?.isOnlyOnePerSample
          ? this.$t('W: Kun én pr. prøve')
          : undefined

      if (this.sampleHasOnlyOnePerSample(sampleIndex)) {
        return selectedIDs
          .map((id) => this.allAnalyses.find((x) => x.id === id))
          .filter((x) => x !== undefined)
          .map((analysis) => ({
            value: analysis.id,
            text: analysis.getName(this.currentLocale),
            subtitle: onlyOnePerSampleSubtitle(analysis),
          }))
      }

      return this.allAnalyses
        .filter((analysis) => {
          // Only show analyses linked to this sample area
          if (!analysis.sampleAreaIDs.includes(sample.sampleAreaID))
            return false
          // Rule: sample already has analyses — exclude all "only one per sample" analyses
          if (selectedIDs.length > 0 && analysis.options?.isOnlyOnePerSample)
            return false
          // Pricing gate: only show analyses enabled in the customer's effective pricing
          if (filter !== null && !filter.has(analysis.id)) return false
          // Must have at least one test configured
          if (analysis.analysisTestIDs.length === 0) return false
          return true
        })
        .map((analysis) => ({
          value: analysis.id,
          text: analysis.getName(this.currentLocale),
          subtitle: onlyOnePerSampleSubtitle(analysis),
        }))
        .sort((a, b) => a.text.localeCompare(b.text))
    },

    sampleHasOnlyOnePerSample(sampleIndex: number): boolean {
      const selectedIDs = this.form.samples[sampleIndex]?.analysisIDs ?? []
      return selectedIDs.some((id) => {
        const analysis = this.allAnalyses.find((x) => x.id === id)
        return analysis?.options?.isOnlyOnePerSample === true
      })
    },

    getAnalysisName(analysisID: string): string {
      const analysis = this.allAnalyses.find((x) => x.id === analysisID)
      return analysis ? analysis.getName(this.currentLocale) : analysisID
    },

    tatOptionsForSample(
      sampleIndex: number
    ): { value: string; text: string }[] {
      return this.availableTatsForSample(sampleIndex).map((tat) => ({
        value: tat.id,
        text: tat.getName(this.currentLocale),
      }))
    },

    // Both read the lab's clock and closed dates — see labDropPointWalkIn.ts.
    isDropPointOpenToday(dp: DropPoint): boolean {
      return isDropPointOpenToday(dp, this.closedDates)
    },

    isWithinDropPointWalkInWindow(dp: DropPoint): boolean {
      return isWithinDropPointWalkInWindow(dp)
    },

    hasOpenWalkInDropPoint(tatID: string): boolean {
      return this.labDropPoints.some(
        (dp) =>
          dp.activeState === 'active' &&
          dp.options.useOpeningHours &&
          dp.walkInTatIDs.includes(tatID) &&
          this.isDropPointOpenToday(dp) &&
          this.isWithinDropPointWalkInWindow(dp)
      )
    },

    availableTatsForSample(sampleIndex: number): TurnaroundTime[] {
      const sample = this.form.samples[sampleIndex]
      if (!sample || sample.analysisIDs.length === 0) return []

      // Check if a TAT meets the min/max constraints of all analyses in this sample
      const meetsConstraints = (tat: TurnaroundTime): boolean => {
        if (tat.type === 'walk-in' && !this.hasOpenWalkInDropPoint(tat.id))
          return false
        return sample.analysisIDs.every((analysisID) => {
          const analysis = this.allAnalyses.find((x) => x.id === analysisID)
          if (!analysis) return true
          const minimumTat = this.allTATs.find(
            (x) => x.id === analysis.minimumTurnaroundTimeID
          ) as TurnaroundTime | undefined
          const maximumTat = this.allTATs.find(
            (x) => x.id === analysis.maximumTurnaroundTimeID
          ) as TurnaroundTime | undefined
          return tat.isCompatibleWithMinMax({ minimumTat, maximumTat })
        })
      }

      const buildPricingGatedTats = (
        segmentTats: {
          tatID: string
          defaultPrice: number | null
          entries: { applicableAnalysisIDs: string[] }[]
          entryType?: string
        }[],
        customerTats: {
          tatID: string
          defaultAction?: string | null
          entries: { applicableAnalysisIDs: string[] }[]
        }[]
      ): TurnaroundTime[] => {
        const tatMap = new Map<
          string,
          { applicability: string; applicableAnalysisIDs: string[] }
        >()
        for (const entry of segmentTats) {
          const isCustom =
            entry.defaultPrice === null && entry.entries.length > 0
          tatMap.set(entry.tatID, {
            applicability: isCustom ? 'custom' : 'all',
            applicableAnalysisIDs: entry.entries.flatMap(
              (e) => e.applicableAnalysisIDs
            ),
          })
        }
        for (const entry of customerTats) {
          // Only a FULLY disabled TAT (disabled for everything, nothing carved
          // out) drops off the list — one disabled for just some analyses is
          // still selectable for the rest.
          if (
            entry.defaultAction === 'disabled' &&
            entry.entries.length === 0
          ) {
            tatMap.delete(entry.tatID)
          } else if (!tatMap.has(entry.tatID)) {
            tatMap.set(entry.tatID, {
              applicability: 'all',
              applicableAnalysisIDs: [],
            })
          }
        }
        return this.allTATs.filter((tat) => {
          const config = tatMap.get(tat.id)
          if (!config) return false
          if (config.applicability === 'custom') {
            const hasApplicable = sample.analysisIDs.some((id) =>
              config.applicableAnalysisIDs.includes(id)
            )
            if (!hasApplicable) return false
          }
          return meetsConstraints(tat)
        })
      }

      let tats: TurnaroundTime[]
      if (this.form.customerType === 'insurance' && this.form.insuranceCustomerAccountID) {
        tats = buildPricingGatedTats(
          this.selectedInsuranceCustomerSegment?.tats ?? [],
          this.selectedInsuranceCustomerPriceDoc?.tats ?? []
        )
      } else if (this.form.segment === 'business' && this.form.customerAccountID) {
        tats = buildPricingGatedTats(
          this.selectedCustomerSegment?.tats ?? [],
          this.selectedCustomerAccountPriceDoc?.tats ?? []
        )
      } else if (this.form.segment === 'private' && this.form.privateUserID) {
        tats = buildPricingGatedTats(
          this.selectedPrivateCustomerSegment?.tats ?? [],
          this.selectedPrivateCustomerPriceDoc?.tats ?? []
        )
      } else {
        tats = this.allTATs.filter(meetsConstraints)
      }

      return tats
    },

    // Single call — the backend resolves every candidate date's DHL rates check
    // in parallel and returns the next available dates already filtered/deduped.
    // Manages isLoadingDeliveryDates itself so it reads correctly regardless of
    // whether the fetch was triggered by the form.dropPointID watcher or an
    // explicit await in onNextClick.
    async fetchAvailableDeliveryDates(): Promise<void> {
      const labAccountID = this.labAccount?.id
      const dp = this.selectedDropPoint
      if (!labAccountID || !dp || dp.type !== 'private') return

      this.isLoadingDeliveryDates = true
      try {
        const result = await managementApi(
          'get',
          '/lab-accounts/{labAccountID}/integrations/dhl/drop-point-capability/{dropPointID}/available-dates',
          { path: { labAccountID, dropPointID: dp.id } }
        )
        this.availableDates = result.success ? result.data.dates : []
      } catch {
        this.availableDates = []
      } finally {
        this.isLoadingDeliveryDates = false
      }
    },

    formatDhlCutoffTime(cutoffDateAndTime: string): string {
      try {
        const date = new Date(cutoffDateAndTime)
        return new Intl.DateTimeFormat(
          this.currentLocale === 'da' ? 'da-DK' : 'en-GB',
          { hour: '2-digit', minute: '2-digit', hour12: false }
        ).format(date)
      } catch {
        return cutoffDateAndTime
      }
    },

    formatDhlPickupWindow(deliveryDate: Date): string {
      const dp = this.selectedDropPoint
      if (!dp || dp.type !== 'private') return ''
      const capability = this.capabilityForDeliveryDate(deliveryDate)
      if (!capability) return ''
      const { pickupEarliest, pickupLatest } = capability
      if (!pickupEarliest || !pickupLatest) return ''
      // pickupEarliest/pickupLatest are HH:MM:SS strings — trim to HH:MM
      const fmt = (t: string) => t.slice(0, 5)
      return this.$t('area.dhl.shipment.message.pickup-window', {
        from: fmt(pickupEarliest),
        to: fmt(pickupLatest),
      })
    },

    async searchServicePoints(): Promise<void> {
      const labAccountID = this.labAccount?.id
      if (!labAccountID) return
      const address = this.servicePointSearchAddress.trim()
      if (!address) {
        this.servicePointSearchError = this.$t('area.dhl.service-point.error.enter-address')
        return
      }
      this.isSearchingServicePoints = true
      this.servicePointSearchError = ''
      this.servicePointSearchResults = []
      try {
        const result = await managementApi(
          'get',
          '/lab-accounts/{labAccountID}/integrations/dhl/service-points',
          {
            path: { labAccountID },
            query: { address, countryCode: 'DK' },
          }
        )
        if (!result.success) {
          this.servicePointSearchError = this.$t(
            'area.dhl.service-point.error.fetch-failed'
          )
          return
        }
        const servicePoints = result.data
        if (servicePoints.length === 0) {
          this.servicePointSearchError = this.$t(
            'area.dhl.service-point.error.none-found'
          )
          return
        }
        this.servicePointSearchResults = servicePoints
      } catch {
        this.servicePointSearchError = this.$t(
          'area.dhl.service-point.error.fetch-failed'
        )
      } finally {
        this.isSearchingServicePoints = false
      }
    },

    selectServicePoint(
      sp: (typeof this.servicePointSearchResults)[number]
    ): void {
      this.selectedServicePoint = sp
      this.form.dropPointID = ''
    },

    // Best-effort — the order itself was already created successfully by this point, so a
    // failure here shouldn't read as a failed order creation. Separate toast instead.
    async markSamplesReceivedIfRequested(
      orderID: string,
      sampleIDs: string[]
    ): Promise<void> {
      if (!this.form.isReceived) return
      try {
        await this.registerSamples(
          orderID,
          sampleIDs,
          undefined,
          this.form.receivingLocationID || undefined
        )
        useToast().success(this.$t('W: Prøverne blev markeret som modtaget'))
      } catch {
        useToast().error(
          this.$t('W: Ordren blev oprettet, men prøverne kunne ikke markeres som modtaget')
        )
      }
    },

    onToggleReceivedClick(): void {
      this.form.isReceived = !this.form.isReceived
      if (!this.form.isReceived) {
        this.form.receivingLocationID = ''
        return
      }
      const assignedLocationID = this.currentUser?.assignedLocationID ?? ''
      this.form.receivingLocationID = this.activeLabLocations.some(
        (location) => location.id === assignedLocationID
      )
        ? assignedLocationID
        : ''
    },

    prevStep(): void {
      if (this.currentStep === 4) {
        this.form.deliveryDate = null
        this.form.isReceived = false
        this.form.receivingLocationID = ''
        // Force a fresh fetch on the next forward pass into this step — cutoffs,
        // prices and dates can change while the customer is elsewhere in the modal.
        this.availableDates = null
      }
      if (this.currentStep === 3) {
        this.form.dropPointID = ''
        this.selectedServicePoint = null
        this.servicePointSearchResults = []
        this.servicePointSearchError = ''
        // dhlCutoffCapabilityByDropPointID lives in dropPointStore and is intentionally
        // not cleared here — it's cached per drop point for the whole session, so going
        // Back then Next again (or opening a second order modal) reuses it, never refetches.
      }
      this.sampleErrors = this.form.samples.map(() => '')
      this.currentStep -= 1
    },

    async onCreate(): Promise<void> {
      this.isLoading = true
      this.errorText = ''
      try {
        // MIL-401: the pending business user is created here — at submit — never when the inline
        // form is filled in, so a wizard that is *abandoned* halfway leaves no orphan user
        // behind. A failure here aborts the whole submit: no order is created either.
        //
        // The reverse is NOT covered: the user is committed before the order, so if order
        // creation, DHL booking or any later validation fails, the user survives with no order
        // attached. Closing that needs both writes behind one server-side operation, or a
        // compensating delete — a real design decision, not a local tweak. Raised in review of
        // lab-management#82 and left for the developer; do not read the paragraph above as a
        // guarantee that no orphan user can ever exist.
        if (this.isCreatingNewCustomerUser) {
          const customer = this.selectedCompanyCustomer
          if (!customer) throw new Error('No customer selected')
          const newUserResult = await this.createCustomerUser(
            customer,
            this.newCustomerUser
          )
          if (!newUserResult.success || !newUserResult.id) {
            this.errorText =
              newUserResult.code === 'already-exists'
                ? getErrorMessage('auth/email-already-exists')
                : getErrorMessage(newUserResult.code ?? '')
            useToast().error(this.$t('W: Kunne ikke oprette brugeren'))
            return
          }
          this.form.customerUserID = newUserResult.id
          this.resetNewCustomerUser()

          // The user exists either way — a failed welcome mail is said out loud, since nothing
          // else in this flow would show it.
          if (newUserResult.welcomeEmailFailed) {
            useToast().error(
              this.$t(
                'W: Bruger oprettet, men velkomstmailen kunne ikke sendes'
              )
            )
          }
        }

        const pad = (n: number) => String(n).padStart(2, '0')

        if (this.selectedServicePoint) {
          // DHL service point — intentionally no delivery date picker, always hand over today.
          // Unlike a private drop point, the customer drops off in person, so there's no DHL
          // pickup schedule to pick a date against. The expected-result date shown from this is
          // just a starting estimate anyway — it updates once the lab registers the sample, same
          // as everywhere else. Decided 2026-08-11, see DHL Order Flow plan (Phase 2).
          const sp = this.selectedServicePoint
          const today = new Date()
          const dateStr = `${today.getFullYear()}-${pad(today.getMonth() + 1)}-${pad(today.getDate())}`
          const timeSlot = { date: dateStr, hours: 0, minutes: 0 }
          const dropPointSnapshot = {
            type: 'dhl-service-point' as const,
            servicePointID: sp.servicePointID,
            name: sp.name,
            address: sp.address,
          }

          const { orderID, dhlShipment, sampleIDs } = await this.createOrder({
            ...buildCreateOrderCustomerFields(this.form),
            priority: this.form.priority,
            caseNumber: this.form.caseNumber,
            address: this.form.address,
            labPrefix: this.generatedPrefix ?? '',
            samples: this.buildSamplesPayload(),
            // The top-level dropPoint snapshot only ever represents a lab/private
            // drop point — a DHL service point is carried on sampleHandOver.dropPoint only.
            dropPoint: null,
            sampleHandOver: {
              method: 'drop-point' as const,
              dropPoint: dropPointSnapshot,
              dropoff: timeSlot,
              pickup: timeSlot,
            },
          })
          this.createdOrderID = orderID
          this.createdOrderDhlShipmentOverride = dhlShipment
          await this.markSamplesReceivedIfRequested(orderID, sampleIDs)
          this.currentStep = 5
          this.showSaveIcon = true
          setTimeout(() => {
            this.showSaveIcon = false
          }, this.defaultMessageSaveIconTime)
          this.unsavedGuard.takeSnapshot()
          useToast().success(this.$t('area.dhl.order.message.created'))
          this.onSuccess?.(orderID)
          return
        }

        const dp =
          this.labDropPoints.find((x) => x.id === this.form.dropPointID) ??
          this.privateDropPoints.find((x) => x.id === this.form.dropPointID)
        if (!dp) throw new Error('No drop point selected')
        const deliveryDate = this.form.deliveryDate
        if (!deliveryDate) throw new Error('No delivery date selected')

        const dateStr = `${deliveryDate.getFullYear()}-${pad(deliveryDate.getMonth() + 1)}-${pad(deliveryDate.getDate())}`

        let timeSlot: { date: string; hours: number; minutes: number }
        if (dp.type === 'private') {
          timeSlot = { date: dateStr, hours: 0, minutes: 0 }
        } else {
          type DayKey =
            | 'monday'
            | 'tuesday'
            | 'wednesday'
            | 'thursday'
            | 'friday'
          const weekdayToKey: (DayKey | null)[] = [
            null,
            'monday',
            'tuesday',
            'wednesday',
            'thursday',
            'friday',
            null,
          ]
          const dayKey = weekdayToKey[deliveryDate.getDay()]
          if (!dayKey) throw new Error('Invalid delivery date weekday')
          const closeTime = dp.openingHours[dayKey].close
          timeSlot = {
            date: dateStr,
            hours: closeTime.hours,
            minutes: closeTime.minutes,
          }
        }
        const dropPointSnapshot = {
          id: dp.id,
          type: dp.type,
          title: dp.getName(this.currentLocale),
          address: {
            addressLine: dp.address.address,
            postalCode: dp.address.postalCode,
            city: dp.address.city,
          },
          placement: dp.getPlacement(this.currentLocale),
        }

        const { orderID, dhlShipment, sampleIDs } = await this.createOrder({
          ...buildCreateOrderCustomerFields(this.form),
          priority: this.form.priority,
          caseNumber: this.form.caseNumber,
          address: this.form.address,
          labPrefix: this.generatedPrefix ?? '',
          samples: this.buildSamplesPayload(),
          dropPoint: dropPointSnapshot,
          sampleHandOver: {
            method: 'drop-point' as const,
            dropPoint: dropPointSnapshot,
            dropoff: timeSlot,
            pickup: timeSlot,
          },
        })
        this.createdOrderID = orderID
        this.createdOrderDhlShipmentOverride = dhlShipment
        await this.markSamplesReceivedIfRequested(orderID, sampleIDs)
        this.unsavedGuard.takeSnapshot()
        useToast().success(this.$t('area.dhl.order.message.created'))
        this.onSuccess?.(orderID)
        if (dhlShipment) {
          // Stay open on step 5 — a private drop point order that got a DHL
          // pickup booked needs its label shown here too, same as the
          // service-point branch above, since it also has to be printed and
          // attached before hand-over.
          this.currentStep = 5
          this.showSaveIcon = true
          setTimeout(() => {
            this.showSaveIcon = false
          }, this.defaultMessageSaveIconTime)
        } else {
          // No label to print (lab drop point, or a private drop point with no
          // DHL pickup) — the success toast is enough, close immediately.
          this.closeTop()
        }
      } catch (err) {
        console.error('[CreateOrderModal] onCreate failed:', err)
        this.errorText =
          err instanceof Error && err.message
            ? err.message
            : this.$t('W: Der opstod en fejl')
        useToast().error(this.$t('W: Kunne ikke oprette ordre'))
      } finally {
        this.isLoading = false
      }
    },
  },

  mounted() {
    this.nowTickerInterval = setInterval(() => {
      this.now = new Date()
    }, 60_000)
    // Start fetching DHL cutoff capability for every private drop point as soon as
    // the modal opens — the customer isn't picked yet, so this can't be narrowed to
    // availablePrivateDropPoints. Results are cached in the store for the session,
    // so by the time step 2's Next is clicked, most (if not all) are already resolved.
    if (this.isDhlEnabled) {
      void this.fetchDhlCutoffCapability(this.privateDropPoints.map((dp) => dp.id))
    }
    // The modal is always the top entry at the moment it is created, so this
    // is its own ModalEntry id — it lets ModalHost tell "the modal I am about
    // to close is dirty" apart from "something underneath it is dirty".
    this.unsavedGuard.register({
      // newCustomerUser is included because it holds real draft work the user
      // would lose on close, even though it lives outside form (MIL-401).
      snapshot: () => ({ ...this.form, ...this.newCustomerUser }),
      modalId: useModalStore().top?.id,
    })
  },

  beforeUnmount() {
    if (this.nowTickerInterval !== null) clearInterval(this.nowTickerInterval)
    this.unsavedGuard.unregister()
  },
}
</script>

